if (typeof window.initProductMediaGallery !== "function") {
  window.initProductMediaGallery = function initProductMediaGallery(section) {
    const allowedSections = ["product-section", "featured-product-section"];

    if (
      !section ||
      !allowedSections.some((cls) => section.classList.contains(cls))
    ) {
      return;
    }

    // Every observer / listener / timer registers its disposer here so
    // shopify:section:unload can tear the section down completely.
    const registerCleanup = (target, dispose) => {
      if (!target) return;
      if (!target.__productCleanups) target.__productCleanups = [];
      target.__productCleanups.push(dispose);
    };

    const runCleanup = (target) => {
      if (!target || !target.__productCleanups) return;
      target.__productCleanups.forEach((dispose) => {
        try {
          dispose();
        } catch (error) {
          /* the node is already gone */
        }
      });
      target.__productCleanups = [];
    };

    const initMainSlider = (section) => {
      if (
        !section ||
        !allowedSections.some((cls) => section.classList.contains(cls))
      ) {
        return;
      }

      const sliderEl = section.querySelector(".js-media-list");
      if (!sliderEl) return;

      const desktopType = sliderEl.dataset?.desktopType || "slider_previews";
      const mobileType = sliderEl.dataset?.mobileType || "slider_previews";
      const hasDesktopZoom = sliderEl.dataset?.hasDesktopZoom === "true";
      const paginationEl = sliderEl.querySelector(".slider-pagination");
      const nextBtn = sliderEl.querySelector(".slider-nav-button--next");
      const prevBtn = sliderEl.querySelector(".slider-nav-button--prev");
      const mediaBox = sliderEl.closest(".product__media");
      const thumbsSlider = mediaBox?.querySelector(".js-media-sublist");

      // Disable loop and set speed to 0 for large slide counts to reduce DOM overhead.
      const slideCount = sliderEl.querySelectorAll(".swiper-slide").length;
      const loopEnabled = sliderEl.dataset?.enableLoop !== "false";
      const isLoop = loopEnabled && slideCount > 1 && slideCount <= 32;
      const speed = slideCount > 32 ? 0 : 800;
      // Plain slider only - the preview-strip gallery keeps one full-width slide
      const mobilePeek = mobileType === "slider";

      // Loop mode re-parents slides as it moves, so a slide that takes focus is
      // pulled out from under it (focus drops to <body>) and the DOM order stops
      // matching the visual one. Swiper's own focus sliding is off for it and Tab
      // is walked here: in on the active slide, one real slide per step, and out of
      // the gallery past either end.
      const bindLoopFocus = (swiper) => {
        sliderEl._loopFocusAbort?.abort();
        if (!isLoop) return;
        const controller = new AbortController();
        sliderEl._loopFocusAbort = controller;
        const { signal } = controller;

        const slideByIndex = (realIndex) =>
          swiper.slides.find(
            (slide) => Number(slide.dataset.swiperSlideIndex) === realIndex
          );

        const focusSlide = (realIndex, last = false) => {
          const focusables = getFocusableElements(slideByIndex(realIndex));
          const target = last
            ? focusables[focusables.length - 1]
            : focusables[0];
          target?.focus({ preventScroll: true });
          // focusing a parked slide scrolls the overflow box under the track
          sliderEl.scrollLeft = 0;
        };

        const leaveGallery = (backwards) => {
          const all = getFocusableElements(document.body);
          const inside = all.filter((el) => sliderEl.contains(el));
          if (!inside.length) return;
          const edge = all.indexOf(
            backwards ? inside[0] : inside[inside.length - 1]
          );
          const candidates = backwards
            ? all.slice(0, edge).reverse()
            : all.slice(edge + 1);
          for (const el of candidates) {
            el.focus();
            if (document.activeElement === el) return;
          }
        };

        sliderEl.addEventListener(
          "keydown",
          (event) => {
            if (event.key !== "Tab") return;
            const slide = event.target.closest(".swiper-slide");
            if (!slide) return;

            // controls inside one slide (video, 3D model) keep the native order
            const focusables = getFocusableElements(slide);
            const edge = event.shiftKey
              ? focusables[0]
              : focusables[focusables.length - 1];
            if (edge && !edge.contains(event.target)) return;

            event.preventDefault();
            const next =
              Number(slide.dataset.swiperSlideIndex) +
              (event.shiftKey ? -1 : 1);
            if (next < 0 || next >= slideCount) {
              leaveGallery(event.shiftKey);
              return;
            }
            swiper.slideToLoop(next);
            focusSlide(next, event.shiftKey);
          },
          { signal }
        );

        // Entering from outside lands on whichever slide sits first or last in the
        // DOM — usually one parked off-screen — so it is moved to the active one.
        let pointerFocus = false;
        sliderEl.addEventListener(
          "pointerdown",
          () => {
            pointerFocus = true;
          },
          { signal }
        );
        sliderEl.addEventListener(
          "focusin",
          (event) => {
            const fromPointer = pointerFocus;
            pointerFocus = false;
            if (fromPointer) return;
            if (event.relatedTarget && sliderEl.contains(event.relatedTarget)) {
              return;
            }
            const slide = event.target.closest(".swiper-slide");
            if (!slide) return;
            if (Number(slide.dataset.swiperSlideIndex) === swiper.realIndex) {
              return;
            }
            focusSlide(swiper.realIndex);
          },
          { signal }
        );
      };

      const initSwiper = () => {
        if (sliderEl.swiper) return;

        const swiper = new Swiper(sliderEl, {
          slidesPerView: 1,
          // Every gallery type shows one slide at a time, so a gap is never a gutter -
          // it only ever shows as a seam between the outgoing and incoming slide
          spaceBetween: 0,
          autoHeight: false,
          loop: isLoop,
          a11y: {
            scrollOnFocus: !isLoop,
          },
          direction: "horizontal",
          speed: speed,
          allowTouchMove: true,
          watchSlidesProgress: true,
          mousewheel: {
            forceToAxis: true,
          },
          // Shared controls cluster: progress line + arrows. CSS shows them for
          // the slider gallery types only.
          pagination: {
            el: paginationEl,
            type: "progressbar",
          },
          navigation: {
            nextEl: nextBtn,
            prevEl: prevBtn,
            disabledClass: "swiper-button-disabled",
          },
          // Phones show the next image peeking so the swipe is discoverable; the
          // slide width comes from CSS, hence slidesPerView 'auto'. Swiper applies
          // ONE matching breakpoint over the base params, so every entry above the
          // peek has to state the plain values again.
          breakpoints: {
            0: {
              slidesPerView: mobilePeek ? "auto" : 1,
              spaceBetween: mobilePeek ? 2 : 0,
            },
            990: {
              slidesPerView: 1,
              spaceBetween: 0,
              allowTouchMove: !hasDesktopZoom,
            },
          },
          thumbs: {
            swiper: thumbsSlider?.swiper ? thumbsSlider.swiper : "",
            multipleActiveThumbs: false,
            slideThumbActiveClass: "swiper-slide-thumb-active",
          },
          on: {
            slideChange: function () {
              window.pauseAllMedia(sliderEl);
              this.params.noSwiping = false;

              if (thumbsSlider?.swiper) {
                const activeIndex = isLoop ? this.realIndex : this.activeIndex;
                thumbsSlider.swiper.slideTo(activeIndex);
              }
            },
            slideChangeTransitionEnd: function () {
              const activeIndex = isLoop ? this.realIndex : this.activeIndex;
              const model3D =
                this.slides[activeIndex]?.querySelector("model-viewer");
              const posterBtn3D = this.slides[activeIndex]?.querySelector(
                ".shopify-model-viewer-ui__button--poster"
              );
              if (model3D && posterBtn3D) {
                posterBtn3D.removeAttribute("hidden");
              }
            },
            touchStart: function () {
              const activeIndex = isLoop ? this.realIndex : this.activeIndex;
              const model3D =
                this.slides[activeIndex]?.querySelector("model-viewer");
              if (model3D) {
                if (
                  !model3D.classList.contains(
                    "shopify-model-viewer-ui__disabled"
                  )
                ) {
                  this.params.noSwiping = true;
                  this.params.noSwipingClass = "swiper-slide";
                } else {
                  this.params.noSwiping = false;
                }
              }
            },
          },
        });
        bindLoopFocus(swiper);
      };

      const destroySwiper = () => {
        sliderEl._loopFocusAbort?.abort();
        if (sliderEl.swiper) {
          sliderEl.swiper.destroy(true, true);
        }
        sliderEl.querySelectorAll(".swiper-wrapper").forEach((wrapper) => {
          wrapper.removeAttribute("style");
        });
        sliderEl.querySelectorAll(".swiper-slide").forEach((slide) => {
          slide.removeAttribute("style");
        });
      };

      const hasDesktopSwiper =
        desktopType === "slider" || desktopType == "slider_previews";
      const hasMobileSwiper =
        mobileType === "slider" || mobileType == "slider_previews";

      if (hasDesktopSwiper && hasMobileSwiper) {
        initSwiper();
      } else {
        const resizeObserver = new ResizeObserver(() => {
          if (window.innerWidth >= 990 && hasDesktopSwiper) {
            initSwiper();
            return;
          }

          if (window.innerWidth < 990 && hasMobileSwiper) {
            initSwiper();
            return;
          }

          destroySwiper();
        });

        resizeObserver.observe(section);
        registerCleanup(section, () => resizeObserver.disconnect());
      }

      registerCleanup(section, destroySwiper);
    };

    const initSubSlider = (section) => {
      if (
        !section ||
        !allowedSections.some((cls) => section.classList.contains(cls))
      ) {
        return;
      }

      const sliderEl = section.querySelector(".js-media-sublist");
      if (!sliderEl) return;

      const desktopType = sliderEl.dataset?.desktopType || "slider_previews";
      const mobileType = sliderEl.dataset?.mobileType || "slider_previews";
      const hasDesktopSwiper = desktopType === "slider_previews";
      const hasMobileSwiper = mobileType === "slider_previews";
      const isRtlPage = document.documentElement.dir === "rtl";

      const initSwiper = () => {
        if (sliderEl.swiper) return sliderEl.swiper;

        return new Swiper(sliderEl, {
          spaceBetween: 8,
          slidesPerView: "auto",
          loop: false,
          direction: "horizontal",
          allowTouchMove: true,
          watchSlidesProgress: true,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          autoHeight: false,
          slideToClickedSlide: true,
          on: {
            touchEnd: function (swiper) {
              let range = 5;
              let diff = (swiper.touches.diff = swiper.isHorizontal()
                ? swiper.touches.currentX - swiper.touches.startX
                : swiper.touches.currentY - swiper.touches.startY);
              if (diff < range || diff > -range) swiper.allowClick = true;
            },
          },
          breakpoints: {
            576: {
              slidesPerView: "auto",
            },
            990: {
              slidesPerView: "auto",
              spaceBetween: 16,
              direction: "vertical",
              allowTouchMove: isRtlPage ? false : true,
            },
          },
        });
      };

      if (hasDesktopSwiper || hasMobileSwiper) {
        initSwiper();

        const resizeObserver = new ResizeObserver(() => {
          if (sliderEl.swiper) {
            if (window.innerWidth >= 990 && hasDesktopSwiper) {
              sliderEl.swiper.enable();
              sliderEl.swiper.changeDirection("vertical", true);
            }

            if (window.innerWidth < 990 && hasMobileSwiper) {
              sliderEl.swiper.enable();
              sliderEl.swiper.changeDirection("horizontal", true);
            }
          }
        });

        resizeObserver.observe(section);

        registerCleanup(section, () => {
          resizeObserver.disconnect();
          if (sliderEl.swiper) sliderEl.swiper.destroy(true, true);
        });
      }
    };

    // ModalDialog relocates the zoom modal to <body>, so it survives the markup a
    // theme-editor re-render replaces and the copy the fresh render appends lands
    // beside the old one. Every path to the modal goes through these two.
    const zoomSlidersFor = (section) => {
      if (!section) return [];
      const sectionBox =
        section.querySelector('[id^="MainProduct-"]') ||
        section.querySelector('[id^="FeaturedProduct-"]');
      const sectionId = sectionBox?.dataset?.section;
      if (!sectionId) return [];

      return Array.from(
        document.querySelectorAll(
          `[data-section-id='${sectionId}'].js-popup-slider`
        )
      );
    };

    const removeZoomModal = (sliderEl) => {
      if (sliderEl.swiper) sliderEl.swiper.destroy(true, true);
      const modal = sliderEl.closest("product-modal");
      if (modal) modal.remove();
      else sliderEl.remove();
    };

    const initZoomSlider = (section) => {
      if (
        !section ||
        !allowedSections.some((cls) => section.classList.contains(cls))
      ) {
        return;
      }

      // Keeping the newest copy is what lets a settings change through: a stale one
      // answers for the same section id and would go on serving the old settings.
      const sliders = zoomSlidersFor(section);
      const sliderEl = sliders.pop();
      sliders.forEach(removeZoomModal);
      if (!sliderEl || sliderEl.swiper) return;

      const paginationEl = sliderEl.querySelector(".slider-pagination");
      const nextBtn = sliderEl.querySelector(".slider-nav-button--next");
      const prevBtn = sliderEl.querySelector(".slider-nav-button--prev");
      const boundToggles = [];

      const onMainSliderToggleClick = (event) => {
        if (!sliderEl.swiper) return;
        const zoomToggle = event.target.closest(".product__media-toggle");
        if (!zoomToggle) return;
        const mediaId = zoomToggle.dataset?.mediaId;
        if (!mediaId) return;
        const slideIndex = sliderEl.swiper.slides.findIndex((slide) =>
          slide.querySelector(`[data-media-id="${mediaId}"]`)
        );
        if (slideIndex !== -1) sliderEl.swiper.slideTo(slideIndex, 0);
      };

      // Filmstrip (design): every media at full dialog height, so the slide widths
      // follow the media ratios and the strip never loops back on itself.
      new Swiper(sliderEl, {
        slidesPerView: "auto",
        spaceBetween: 4,
        speed: 800,
        slideToClickedSlide: true,
        mousewheel: {
          forceToAxis: true,
        },
        // Same controls cluster as the gallery slider types.
        pagination: {
          el: paginationEl,
          type: "progressbar",
        },
        navigation: {
          nextEl: nextBtn,
          prevEl: prevBtn,
          disabledClass: "swiper-button-disabled",
        },
        on: {
          afterInit: function () {
            section
              .querySelectorAll(".product__media-list .product__media-toggle")
              .forEach((elem) => {
                elem.addEventListener("click", onMainSliderToggleClick);
                boundToggles.push(elem);
              });
          },
          slideChange: function () {
            window.pauseAllMedia(sliderEl);
            this.params.noSwiping = false;
          },
          slideChangeTransitionEnd: function () {
            const model3D =
              this.slides[this.activeIndex]?.querySelector("model-viewer");
            const posterBtn3D = this.slides[this.activeIndex]?.querySelector(
              ".shopify-model-viewer-ui__button--poster"
            );
            if (model3D && posterBtn3D) {
              posterBtn3D.removeAttribute("hidden");
            }
          },
          touchStart: function () {
            const model3D =
              this.slides[this.activeIndex]?.querySelector("model-viewer");
            if (model3D) {
              if (
                !model3D.classList.contains("shopify-model-viewer-ui__disabled")
              ) {
                this.params.noSwiping = true;
                this.params.noSwipingClass = "swiper-slide";
              } else {
                this.params.noSwiping = false;
              }
            }
          },
        },
      });

      registerCleanup(section, () => {
        boundToggles.forEach((elem) =>
          elem.removeEventListener("click", onMainSliderToggleClick)
        );
        if (sliderEl.swiper) sliderEl.swiper.destroy(true, true);
      });
    };

    // Grid gallery scroll rail. CSS owns the POSITION (position: sticky pins the bar
    // to the viewport centre between the offset spacer and the end of the track), so
    // scrolling only refreshes the fill — a transform write, never a layout one.
    // Desktop only: below 990 the rail is hidden and nothing is bound.
    const initMediaProgress = (section) => {
      if (
        !section ||
        !allowedSections.some((cls) => section.classList.contains(cls))
      ) {
        return;
      }

      const progressEl = section.querySelector(".js-media-progress");
      if (!progressEl) return;

      const trackEl = progressEl.querySelector(
        ".product__media-progress-track"
      );
      const barEl = progressEl.querySelector(".slider-pagination");
      const fillEl = progressEl.querySelector(
        ".swiper-pagination-progressbar-fill"
      );
      if (!trackEl || !barEl || !fillEl) return;

      const desktopQuery = window.matchMedia("(min-width: 990px)");
      let frame = null;
      let bound = false;

      const update = () => {
        frame = null;

        const bounds = trackEl.getBoundingClientRect();
        const barHeight = barEl.offsetHeight;
        const travel = bounds.height - barHeight;
        if (travel <= 0) {
          fillEl.style.setProperty("--slider-progress", "0");
          return;
        }

        const desired = window.innerHeight / 2 - barHeight / 2 - bounds.top;
        const offset = Math.min(Math.max(desired, 0), travel);
        fillEl.style.setProperty(
          "--slider-progress",
          (offset / travel).toFixed(3)
        );
      };

      const onScroll = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(update);
      };

      const unbind = () => {
        if (!bound) return;
        bound = false;
        if (frame) {
          window.cancelAnimationFrame(frame);
          frame = null;
        }
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
      };

      const bind = () => {
        if (bound) return;
        bound = true;
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        update();
      };

      const onBreakpointChange = () => {
        if (desktopQuery.matches) {
          bind();
        } else {
          unbind();
        }
      };

      desktopQuery.addEventListener("change", onBreakpointChange);
      onBreakpointChange();

      registerCleanup(section, () => {
        desktopQuery.removeEventListener("change", onBreakpointChange);
        unbind();
      });
    };

    // Hovering a gallery image hides the pointer and follows it with the zoom button.
    const initZoomCursor = (section) => {
      if (
        !section ||
        !allowedSections.some((cls) => section.classList.contains(cls))
      ) {
        return;
      }

      const toggles = section.querySelectorAll(
        ".product__media-toggle--zoom-cursor"
      );
      if (!toggles.length) return;

      const onPointerMove = (event) => {
        const toggle = event.currentTarget;
        // One cursor per gallery, parked outside the slider, so both the element and the
        // box the offsets are measured against are the gallery's, not this toggle's
        const wrapper = toggle.closest(".product__media-list-wrapper");
        const cursorEl = wrapper?.querySelector(".product__media-zoom-cursor");
        if (!cursorEl) return;
        const bounds = wrapper.getBoundingClientRect();
        cursorEl.style.setProperty(
          "--zoom-cursor-x",
          `${event.clientX - bounds.left}px`
        );
        cursorEl.style.setProperty(
          "--zoom-cursor-y",
          `${event.clientY - bounds.top}px`
        );
      };

      toggles.forEach((toggle) => {
        toggle.addEventListener("pointermove", onPointerMove);
      });

      registerCleanup(section, () => {
        toggles.forEach((toggle) => {
          toggle.removeEventListener("pointermove", onPointerMove);
        });
      });
    };

    initSubSlider(section);
    initMainSlider(section);
    initZoomSlider(section);
    initMediaProgress(section);
    initZoomCursor(section);

    const onSectionLoad = (event) => {
      runCleanup(event.target);
      initSubSlider(event.target);
      initMainSlider(event.target);
      initZoomSlider(event.target);
      initMediaProgress(event.target);
      initZoomCursor(event.target);
    };

    // The relocated modal is the one thing the section cannot take with it
    const onSectionUnload = (event) => {
      runCleanup(event.target);
      zoomSlidersFor(event.target).forEach(removeZoomModal);
    };

    // The script is re-evaluated with every re-render, and it calls in again below
    if (!window.__themeProductGalleryEvents) {
      window.__themeProductGalleryEvents = true;
      document.addEventListener("shopify:section:load", onSectionLoad);
      document.addEventListener("shopify:section:unload", onSectionUnload);
    }
  };
}

if (typeof window.initProductPageElements !== "function") {
  window.initProductPageElements = function initProductPageElements(section) {
    const allowedSections = ["product-section", "featured-product-section"];

    if (
      !section ||
      !allowedSections.some((cls) => section.classList.contains(cls))
    ) {
      return;
    }

    const registerCleanup = (target, dispose) => {
      if (!target) return;
      if (!target.__productElementCleanups)
        target.__productElementCleanups = [];
      target.__productElementCleanups.push(dispose);
    };

    const runCleanup = (target) => {
      if (!target || !target.__productElementCleanups) return;
      target.__productElementCleanups.forEach((dispose) => {
        try {
          dispose();
        } catch (error) {
          /* the node is already gone */
        }
      });
      target.__productElementCleanups = [];
    };

    const initStickyAddBar = (section) => {
      if (!section || !section.classList.contains("product-section")) {
        return;
      }

      const stickyBar = section.querySelector(".product-sticky-add-bar");
      if (!stickyBar) return;

      // The bag stands in for the buy buttons, so it takes over the moment they leave
      // the viewport - which on desktop is when the details column releases its pinned
      // stack, not when the whole product block ends - and stands down again once the
      // footer comes up. With the Buy buttons block removed there is nothing to relieve,
      // so it falls back to the end of the product block.
      const triggerEl =
        section.querySelector(".product__buy-buttons") ||
        section.querySelector(".product__outer");
      const footerEl =
        document.querySelector(
          ".shopify-section-group-footer-group .footer__bottom"
        ) ||
        document.querySelector(
          ".shopify-section-group-footer-group .footer__top"
        );

      if (!triggerEl) return;

      // On wide screens focus leaves the section while the buy buttons are still in view,
      // so the card is down at its DOM slot; at the end of <main> Tab reaches it while it
      // is up. Cleanup puts it back, so an unloaded section takes it along.
      const barHome = stickyBar.parentNode;
      const barHomeNext = stickyBar.nextSibling;
      document.getElementById("MainContent")?.append(stickyBar);

      // The bag is a shortcut to the buy buttons, so it stays down when there is nothing
      // to add. The picker flips this very button (toggleAddButton) once its fetch
      // settles, which is after the variant event, so the attribute is what to follow.
      const addButton = stickyBar.querySelector('[name="add"]');
      const canAdd = () => !addButton || !addButton.hasAttribute("disabled");

      // The card covers the back-to-top button's corner; the button reads this to
      // step over it. Measured rather than assumed: the height follows the title.
      const publishClearance = () => {
        const bottom =
          parseFloat(window.getComputedStyle(stickyBar).bottom) || 0;
        // offsetHeight is 0 while the card is display: none (hidden on mobile)
        const clearance =
          canAdd() && stickyBar.offsetHeight
            ? stickyBar.offsetHeight + bottom
            : 0;
        document.documentElement.style.setProperty(
          "--sticky-bag-clearance",
          `${clearance}px`
        );
      };

      const update = () => {
        const scrolledPast = triggerEl.getBoundingClientRect().bottom <= 0;
        const footerVisible = footerEl
          ? footerEl.getBoundingClientRect().top < window.innerHeight
          : false;

        stickyBar.classList.toggle(
          "active",
          scrolledPast && !footerVisible && canAdd()
        );
      };

      // Both edges of the range are threshold crossings, so the two observed
      // elements cover every transition without a scroll listener
      const observer = new IntersectionObserver(update, {
        root: null,
        rootMargin: "0px",
        threshold: 0,
      });

      observer.observe(triggerEl);
      if (footerEl) observer.observe(footerEl);

      const resizeObserver = new ResizeObserver(publishClearance);
      resizeObserver.observe(stickyBar);

      const buttonObserver = addButton
        ? new MutationObserver(() => {
            publishClearance();
            update();
          })
        : null;
      buttonObserver?.observe(addButton, {
        attributes: true,
        attributeFilter: ["disabled"],
      });

      publishClearance();
      update();

      registerCleanup(section, () => {
        observer.disconnect();
        resizeObserver.disconnect();
        buttonObserver?.disconnect();
        document.documentElement.style.removeProperty("--sticky-bag-clearance");
        barHome.insertBefore(
          stickyBar,
          barHomeNext?.parentNode === barHome ? barHomeNext : null
        );
      });
    };

    // Popup Feature block: the `i` next to the label opens its tooltip on hover
    // (pointer devices) and on click/keyboard everywhere.
    const initFeatureTooltips = (section) => {
      if (
        !section ||
        !allowedSections.some((cls) => section.classList.contains(cls))
      ) {
        return;
      }

      // The popup drawer is relocated outside the section wrapper by <modal-dialog>,
      // so the toggles are resolved from the document on every event.
      const canHover = () => window.matchMedia("(hover: hover)").matches;

      // The tooltip hangs off its own toggle, so a long label starts it far enough along
      // the row to run past the panel - which is flush with the screen edge. Pulled back
      // by the overrun, it keeps its place next to the `i` wherever there is room.
      const clampTooltip = (tooltip) => {
        const row = tooltip.closest(".product-popup-feature");
        if (!row) return;

        tooltip.style.removeProperty("margin-inline-start");

        const isRtl = getComputedStyle(tooltip).direction === "rtl";
        const tooltipRect = tooltip.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        const overrun = isRtl
          ? rowRect.left - tooltipRect.left
          : tooltipRect.right - rowRect.right;

        if (overrun > 0) {
          tooltip.style.marginInlineStart = `${-Math.ceil(overrun)}px`;
        }
      };

      const setTooltipState = (toggle, isOpen) => {
        if (!toggle) return;
        const tooltip = document.getElementById(
          toggle.getAttribute("aria-controls")
        );
        toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
        if (!tooltip) return;

        tooltip.hidden = !isOpen;
        if (isOpen) {
          clampTooltip(tooltip);
        } else {
          tooltip.style.removeProperty("margin-inline-start");
        }
      };

      // The clamp is a measurement, so it goes stale the moment its toggle moves: a web
      // font swapping in after first paint widens the label and carries the tooltip right
      // with it, off the panel again. The label is the element that resizes when the
      // anchor moves, and the row when the panel does - watching both re-runs the clamp
      // for a font swap, an edited label and a resized window alike.
      const reclampOpen = () => {
        document
          .querySelectorAll(".product-popup-feature__tooltip:not([hidden])")
          .forEach(clampTooltip);
      };

      const anchorObserver = new ResizeObserver(reclampOpen);
      document
        .querySelectorAll(
          ".product-popup-feature__headline, .product-popup-feature__label"
        )
        .forEach((el) => anchorObserver.observe(el));

      const closeAll = (except) => {
        document
          .querySelectorAll(".js-feature-tooltip-toggle")
          .forEach((toggle) => {
            if (toggle === except) return;
            setTooltipState(toggle, false);
          });
      };

      const onDocumentClick = (event) => {
        const target = event.target;
        if (!target || typeof target.closest !== "function") return;

        const toggle = target.closest(".js-feature-tooltip-toggle");

        if (toggle) {
          // A real pointer click (detail > 0) on a hover device would close what the
          // pointer just opened; keyboard activation reports detail === 0 and toggles
          if (event.detail > 0 && canHover()) return;
          const isOpen = toggle.getAttribute("aria-expanded") === "true";
          closeAll(toggle);
          setTooltipState(toggle, !isOpen);
          return;
        }

        if (target.closest(".js-feature-tooltip")) return;
        closeAll();
      };

      // mouseenter/mouseleave do not bubble, so hover is delegated with over/out
      const onDocumentOver = (event) => {
        if (!canHover()) return;
        const target = event.target;
        if (!target || typeof target.closest !== "function") return;

        const wrapper = target.closest(".js-feature-tooltip");
        if (!wrapper) return;

        const toggle = wrapper.querySelector(".js-feature-tooltip-toggle");
        if (!toggle) return;
        closeAll(toggle);
        setTooltipState(toggle, true);
      };

      const onDocumentOut = (event) => {
        if (!canHover()) return;
        const target = event.target;
        if (!target || typeof target.closest !== "function") return;

        const wrapper = target.closest(".js-feature-tooltip");
        if (!wrapper) return;

        const next = event.relatedTarget;
        if (next && next.nodeType === 1 && wrapper.contains(next)) return;

        setTooltipState(
          wrapper.querySelector(".js-feature-tooltip-toggle"),
          false
        );
      };

      document.addEventListener("click", onDocumentClick);
      document.addEventListener("mouseover", onDocumentOver);
      document.addEventListener("mouseout", onDocumentOut);
      window.addEventListener("resize", reclampOpen);

      registerCleanup(section, () => {
        document.removeEventListener("click", onDocumentClick);
        document.removeEventListener("mouseover", onDocumentOver);
        document.removeEventListener("mouseout", onDocumentOut);
        window.removeEventListener("resize", reclampOpen);
        anchorObserver.disconnect();
      });
    };

    // Scroll-under details (design: page--product-grid-gallery-onscroll). Every block in
    // the details column holds its place by sticking in DOM order at a running offset. The
    // exception is the Hide on scroll group: it is left out of the stack, so it keeps
    // scrolling and its blocks pass out of sight underneath the ones that stay. Which
    // blocks go is the merchant's call, which is why nothing here is keyed on block type.
    // Only the offsets and the masking bands are measured - the movement itself is
    // position: sticky, so there is nothing to animate.
    const PINNED_CLASS = "product__pinned";
    // The stack's last block, which drops its trailing margin: `:last-child` cannot find it
    // because the column keeps rendering empty blocks after it (complementary products
    // fetches its cards, so its element is there before there is anything in it).
    const TAIL_CLASS = "product__pinned--tail";
    const SCROLL_UNDER_CLASS = "product__info-container--scroll-under";
    const RELEASED_CLASS = "product__info-container--released";
    const HIDE_GROUP_CLASS = "product__hide-on-scroll";
    // Sub-pixel band edges would leave a hairline of scrolling content between two
    // pinned blocks
    const MASK_OVERLAP = 1;
    // The stack counts DOWN from this ceiling, so an open variant dropdown - trapped in
    // the picker's own stacking context - still paints over the buy buttons. The ceiling
    // keeps the range under the sticky add bar (11) and the header (20); the floor keeps
    // it over the sliders and cards that pass underneath (z-index 1). A stack deeper than
    // the range ties at the TOP, where the blocks are plain text with nothing to overlap.
    const PINNED_Z_TOP = 10;
    const PINNED_Z_FLOOR = 2;

    const initScrollUnderDetails = (section) => {
      if (
        !section ||
        !allowedSections.some((cls) => section.classList.contains(cls))
      ) {
        return;
      }

      const infoEl = section.querySelector(".product__info-container");
      if (!infoEl) return;

      const desktopQuery = window.matchMedia("(min-width: 990px)");
      let pinned = [];
      let frame = null;
      let isBlockSelected = false;
      let isReleased = false;
      // How far the stack's lowest margin box reaches below its own top, and the viewport
      // line that put it down - the two numbers the hand-over is decided on
      let stackExtent = 0;
      let releaseLine = 0;
      let releaseFrame = null;
      let flowTops = [];
      let offsets = [];

      const release = (elements) => {
        elements.forEach((el) => {
          el.classList.remove(PINNED_CLASS, TAIL_CLASS);
          el.style.removeProperty("--pinned-offset");
          el.style.removeProperty("--pinned-bleed");
          el.style.removeProperty("--pinned-bleed-below");
          el.style.removeProperty("--pinned-z");
          el.style.removeProperty("--released-top");
        });
      };

      // `position` and `top` flip together on both sides of the handover below. Left to
      // itself the transition on `top` would animate it from a scrollport offset to a
      // column offset, sending the block across the screen.
      const flip = (apply) => {
        pinned.forEach((el) => {
          el.style.transition = "none";
        });
        apply();
        void infoEl.offsetHeight;
        pinned.forEach((el) => {
          el.style.removeProperty("transition");
        });
      };

      // End of the sticky range. Sticky clamps every block to the column's bottom minus
      // its OWN height, so the tall ones overtake the short ones on the way out; instead
      // the stack is frozen where it stands and scrolls away with the column as one piece.
      // Relative, not absolute: the block keeps its box in flow, so nothing is re-sized or
      // re-flowed and the shift lands inside the column, adding no scroll height.
      const applyRelease = () => {
        if (isReleased || !pinned.length) return;

        // Anchored to the column's BOTTOM edge, not to the viewport: the stack parks flush
        // against the column's end, which is where sticky's own clamping would put the block
        // the end meets first - so at the line the parked stack and the pinned one stand in
        // the same place, and nothing is left over underneath.
        const columnHeight = infoEl.getBoundingClientRect().height;
        const shifts = pinned.map(
          (el, index) =>
            columnHeight - stackExtent + offsets[index] - flowTops[index]
        );

        flip(() => {
          pinned.forEach((el, index) => {
            el.style.setProperty(
              "--released-top",
              `${shifts[index].toFixed(2)}px`
            );
          });
          infoEl.classList.add(RELEASED_CLASS);
        });

        isReleased = true;
      };

      const clearRelease = () => {
        if (!isReleased) return;

        flip(() => {
          infoEl.classList.remove(RELEASED_CLASS);
          pinned.forEach((el) => el.style.removeProperty("--released-top"));
        });

        isReleased = false;
      };

      // One rect read against the line sticky would start clamping on. An observer would be
      // cheaper still, but a notification that lands mid-measure is swallowed and no further
      // edge arrives until the column is scrolled out and back in - this cannot go stale.
      const updateRelease = () => {
        if (!pinned.length || !stackExtent) return;
        const bottom = infoEl.getBoundingClientRect().bottom;

        // Handing back on the line it left on keeps the flip invisible in both directions:
        // at that one position the parked stack and the pinned one stand in the same place.
        if (isReleased) {
          if (bottom >= releaseLine) clearRelease();
          return;
        }

        // The base is read off the block rather than stored, because its sticky top follows
        // the header reveal - a line measured once can sit a header height out.
        const base = parseFloat(window.getComputedStyle(pinned[0]).top) || 0;
        const line = base + stackExtent;
        if (bottom >= line) return;

        releaseLine = line;
        applyRelease();
      };

      const onScroll = () => {
        if (releaseFrame) return;
        releaseFrame = window.requestAnimationFrame(() => {
          releaseFrame = null;
          updateRelease();
        });
      };

      const clear = () => {
        clearRelease();
        stackExtent = 0;
        infoEl.classList.remove(SCROLL_UNDER_CLASS);
        release(pinned);
        pinned = [];
      };

      const measure = () => {
        frame = null;
        clearRelease();
        // The stack comes off its sticky footing first. A sticky offset is painted, not
        // laid out, so a pinned block reads back its pinned position rather than the flow
        // one it will scroll away from; nothing paints inside this frame, so stripping and
        // re-applying below is invisible.
        release(pinned);
        pinned = [];

        // Every child, not just the ones that make the stack: a block that starts empty and
        // fills in later - complementary products fetches its cards - is filtered out at
        // zero height here, and would never be measured again if it were left unobserved
        Array.from(infoEl.children).forEach((el) => observer.observe(el));

        if (!desktopQuery.matches || isBlockSelected) {
          clear();
          return;
        }

        // Out-of-flow children (the popup and back-in-stock drawers) have no place in a
        // flow stack, and a row the product renders empty (inventory it does not track)
        // collapses to a zero box and leaves on its own
        const stack = Array.from(infoEl.children).filter((el) => {
          if (el.classList.contains(HIDE_GROUP_CLASS)) return false;
          if (el.getBoundingClientRect().height <= 0) return false;
          const position = window.getComputedStyle(el).position;
          return position !== "fixed" && position !== "absolute";
        });

        // Nothing grouped means no block has to mask anything, so the column is left with
        // the plain sticky behaviour it has without this effect
        const hasGroup = Array.from(
          infoEl.querySelectorAll(`:scope > .${HIDE_GROUP_CLASS}`)
        ).some((el) => el.getBoundingClientRect().height > 0);

        if (!hasGroup || !stack.length) {
          clear();
          return;
        }

        // Sticky clamps on the MARGIN box, so a trailing margin on the block the column's
        // end meets first is both the space left under the parked stack and the distance
        // the hand-over has to happen over. With nothing rendered after it that margin has
        // nothing to space, so it goes - before the measurements below, which have to read
        // the boxes the stack will actually be pinned with.
        const tail = stack[stack.length - 1];
        let isTail = true;
        for (let el = tail.nextElementSibling; el; el = el.nextElementSibling) {
          if (el.getBoundingClientRect().height > 0) {
            isTail = false;
            break;
          }
        }
        if (isTail) tail.classList.add(TAIL_CLASS);

        const columnTop = infoEl.getBoundingClientRect().top;
        flowTops = stack.map(
          (el) => el.getBoundingClientRect().top - columnTop
        );

        const heights = stack.map((el) => el.getBoundingClientRect().height);
        // The blocks sit apart in flow, so the band between two of them once both are
        // pinned is the margin they would collapse to as neighbours
        const marginTops = stack.map(
          (el) => parseFloat(window.getComputedStyle(el).marginTop) || 0
        );
        const marginBottoms = stack.map(
          (el) => parseFloat(window.getComputedStyle(el).marginBottom) || 0
        );
        const gaps = stack.map((el, index) =>
          index ? Math.max(marginBottoms[index - 1], marginTops[index]) : 0
        );

        // Every block outside the group pins, however tall the stack grows. A block the
        // merchant did not put in a Hide on scroll group must never pass under another
        // one, so nothing is dropped to fit the viewport: a stack taller than the fold
        // parks its trailing blocks below it, and the release below is what carries them
        // back into view against the end of the column.
        const count = stack.length;
        const active = stack;

        let offset = 0;
        let clamped = 0;
        offsets = [];
        active.forEach((el, index) => {
          if (index) offset += heights[index - 1] + gaps[index];
          offsets.push(offset);
          // Sticky clamps the MARGIN box, so the first block the column's bottom starts
          // pushing is whichever one's margin box reaches lowest down the stack
          clamped = Math.max(
            clamped,
            offset + heights[index] + marginBottoms[index]
          );

          el.style.setProperty("--pinned-offset", `${offset.toFixed(2)}px`);
          // A block masks its OWN margins, not the band it shares with the block above it:
          // that band is only empty once both are pinned, and in flow the group sits in it,
          // so a band-sized mask cuts into the group's own blocks. Two own-margin masks
          // always add up to at least the band, so nothing shows through once pinned either.
          // The first block has no margin to work with: it falls back to
          // --pinned-bleed-first, the strip up to the header.
          if (index) {
            el.style.setProperty(
              "--pinned-bleed",
              `${marginTops[index].toFixed(2)}px`
            );
          } else {
            el.style.removeProperty("--pinned-bleed");
          }
          // A margin of zero would leave the seam on the block's own edge, where two
          // elements meet and a sub-pixel gap can let the group through
          el.style.setProperty(
            "--pinned-bleed-below",
            `${Math.max(marginBottoms[index], MASK_OVERLAP).toFixed(2)}px`
          );
          el.style.setProperty(
            "--pinned-z",
            `${Math.min(PINNED_Z_TOP, PINNED_Z_FLOOR + count - 1 - index)}`
          );
          el.classList.add(PINNED_CLASS);
        });

        pinned = active;
        infoEl.classList.add(SCROLL_UNDER_CLASS);

        // Pure layout, so the line updateRelease builds from it holds however the header
        // moves the stack's base
        stackExtent = clamped;

        updateRelease();
      };

      const schedule = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(measure);
      };

      // A sticky offset changes no box, so writing the offsets cannot feed this back
      const observer = new ResizeObserver(schedule);
      observer.observe(infoEl);

      // The editor scrolls to the block it selects, so the stack steps aside while the
      // merchant works on a block it would otherwise keep out of sight. Being pinned is
      // no longer proof of that: a stack taller than the fold pins its last blocks below
      // it, and those have to stand down like the group's do.
      const onBlockSelect = (event) => {
        const node = event.target;
        if (!node || !infoEl.contains(node)) return;
        const pin = pinned.find((el) => el === node || el.contains(node));
        if (pin) {
          const rect = pin.getBoundingClientRect();
          if (rect.top >= 0 && rect.bottom <= window.innerHeight) return;
        }
        isBlockSelected = true;
        clear();
      };

      const onBlockDeselect = () => {
        if (!isBlockSelected) return;
        isBlockSelected = false;
        schedule();
      };

      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", schedule, { passive: true });
      desktopQuery.addEventListener("change", schedule);
      document.addEventListener("shopify:block:select", onBlockSelect);
      document.addEventListener("shopify:block:deselect", onBlockDeselect);

      measure();

      registerCleanup(section, () => {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", schedule);
        desktopQuery.removeEventListener("change", schedule);
        document.removeEventListener("shopify:block:select", onBlockSelect);
        document.removeEventListener("shopify:block:deselect", onBlockDeselect);
        if (frame) window.cancelAnimationFrame(frame);
        if (releaseFrame) window.cancelAnimationFrame(releaseFrame);
        observer.disconnect();
        clear();
      });
    };

    initStickyAddBar(section);
    initFeatureTooltips(section);
    initScrollUnderDetails(section);

    const onSectionLoad = (event) => {
      runCleanup(event.target);
      initStickyAddBar(event.target);
      initFeatureTooltips(event.target);
      initScrollUnderDetails(event.target);
    };

    const onSectionUnload = (event) => runCleanup(event.target);

    // The script is re-evaluated with every re-render, and it calls in again below
    if (!window.__themeProductElementsEvents) {
      window.__themeProductElementsEvents = true;
      document.addEventListener("shopify:section:load", onSectionLoad);
      document.addEventListener("shopify:section:unload", onSectionUnload);
    }
  };
}

window.initProductMediaGallery(document.currentScript.parentElement);
window.initProductPageElements(document.currentScript.parentElement);
